import { ComponentProps, HTMLAttributes, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router";
import type { NavChildren, NavigationA } from "@components/Header/types";
import useSendDataToGTM from "@features/analytics/hooks/useSendDataToGTM";
import { useGetMainNavList } from "@hooks/useGetMainNavList";
import { cn, getUrlQuery } from "@util/helpers";

import { navigationConstant } from "./constant";

export type NavigationProps = {
  data?: NavigationA;
  position?: string;
  /** The classname to be added on navigation texts. Usually used to override their text colors. */
  textClassName?: string;
};

export function Navigation({
  data,
  position,
  textClassName,
}: NavigationProps): React.ReactElement {
  const navigationData = data?.length ? data : navigationConstant;
  const findMenuData = useGetMainNavList({ navigationData, position });

  return (
    <nav aria-label={`header-nav-${position}`} className="flex gap-3">
      {findMenuData?.map((item, index) => {
        return (
          <NavItem
            item={item}
            index={index}
            key={item.key}
            position={position}
            textClassName={textClassName}
          />
        );
      })}
    </nav>
  );
}

type NavItemProps = {
  item: NavChildren;
  position?: string;
  index?: number;
  textClassName?: string;
} & Pick<HTMLAttributes<HTMLDivElement>, "className"> &
  Pick<ComponentProps<typeof Navigation>, "textClassName">;

const navItemStyle = cn(
  "p-3 duration-300",
  "lg:hover:bg-gray-250 rounded-[25px]"
);

export function NavItem({
  item,
  className,
  textClassName,
}: NavItemProps): React.ReactElement {
  const {
    label,
    link,
    target,
    extraClass,
    trackingData,
    ref: refParam,
  }: NavChildren = item;
  const [isInView, setInView] = useState(true);
  const sendDataToGTM = useSendDataToGTM();

  const { ref } = useInView({
    threshold: 1,
    onChange: (_, entry) => {
      setInView(entry?.intersectionRatio >= 1);
    },
  });

  const onClick = () => {
    if (trackingData) {
      sendDataToGTM(trackingData);
    }
  };
  const linkParams = new URLSearchParams({ ref: refParam || "header" });
  const query = getUrlQuery(link, linkParams);

  return (
    <Link
      ref={ref}
      data-testid={`navigation-${label.toLowerCase()}`}
      role="link"
      to={link + query}
      className={cn(
        "group invisible relative flex-none font-poppins text-2xs font-semibold leading-150 tracking-2 text-gray-850",
        navItemStyle,
        className,
        { visible: isInView },
        extraClass,
        textClassName
      )}
      target={target ? target : undefined}
      reloadDocument
      onClick={onClick}
    >
      {label}
    </Link>
  );
}
